/* eslint-disable rxjs/no-unbound-methods */
import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Observable, of, throwError, combineLatest } from "rxjs";
import { catchError } from "rxjs/operators";
import {
  CommentUpdate,
  IApprovalData,
  IParentRequest,
  IQuotaImplicationsData,
  IRequestRegion,
  IStatusResponse,
  ISubRequest,
  ISubRequestUpdateData,
  ISubscriptionDetails,
  ISubscriptionInfoInput,
  IUIRequest,
  IUserProfile,
  IWorkItemResponse,
  MyRequestAndWorkItems,
  RegionStatus,
  RequestServiceType,
  SubscriptionZoneInfo,
  Roles,
  ISelfServiceInfoInput,
  ISubRequestWithTicket,
  LionrockUserRoles,
  OkMessage,
} from "../npr-request.model";
import {
  FfpConfiguration,
  NprConfiguration,
  PlannedQuotaConfiguration,
  PfConfiguration,
  ManatreeConfiguration,
} from "../app.configuration";
import { environment } from "src/environments/environment";
import { ParseRequestId } from "../utility/common-helper";
import { GlobalErrorHandler } from "./globalErrorHandler.service";
import {
  App,
  ArmResourceType,
  ApprovalList,
  AutoApprovalRule,
  AZ,
  AZConfig,
  CisJob,
  Config,
  AutoApprovalThreshold,
  HdiSku,
  HoboSubscriptionAutoApprovalRule,
  NewApp,
  OperationLog,
  PlannedQuotaRequestDetail,
  Region,
  EdgeZone,
  RegionConfig,
  EdgeZoneConfig,
  RegionTable,
  ServiceReadiness,
  ServiceReadinessStage,
  Sql,
  SubRequestBatchApproval,
  Team,
  Ticket,
  TrustedService,
  NewTrustedService,
  UserRole,
  VmSize,
  VmSku,
  CustomerRegion,
} from "../generated-models";
import { EnableRegionAccess } from "../generated-models/EnableRegionAccess";
import { RegionSupportInfo } from "../generated-models/RegionSupportInfo";
import { SubscriptionRegion } from "../generated-models/SubscriptionRegion";
import { SubscriptionRegionSupportInfo } from "../generated-models/SubscriptionRegionSupportInfo";
import { SubscriptionSupportInfoARM } from "../generated-models/SubscriptionSupportInfoARM";
import { Discussion } from "../generated-models/Discussion";
import { SubscriptionThreshold } from "../generated-models/SubscriptionThreshold";
import { RegionalQuotaLimit } from "../generated-models/RegionalQuotaLimit";
import { PlanRegion } from "../generated-models/PlanRegion";
import { PlannedQuotaRequest } from "../generated-models/PlannedQuotaRequest";
import { PlanRegionDetail } from "../generated-models/PlanRegionDetail";
import { PlanSubmission } from "../generated-models/PlanSubmission";
import { PlanFile } from "../generated-models/PlanFile";
import { Plan } from "../generated-models/Plan";
import { CommitFfpPlan } from "../generated-models/CommitFfpPlan";
import { PlanRegionOverallApproval } from "../generated-models/PlanRegionOverallApproval";
import { PlanRegionDiff } from "../generated-models/PlanRegionDiff";
import { PlanRegionUpdateData } from "../generated-models/PlanRegionUpdateData";
import { User } from "../generated-models/User";
import { PlanCommit } from "../generated-models/PlanCommit";
import { PlannedQuotaRequestPagination } from "../generated-models/PlannedQuotaRequestPagination";
import { RegionalQuotaStatistics } from "../generated-models/RegionalQuotaStatistics";
import { PlanRegionStatusEnum } from "../shared/enums/plan-enums";
import { SearchRequests } from "../generated-models/SearchRequests";
import { PlanJsonSchema } from "../quota/plans/modals/plan-editor-modal/plan-editor-modal.component";
import { PlannedQuotaSubscriptionBinding } from "../generated-models/PlannedQuotaSubscriptionBinding";
import { PlannedQuotaPropertyBinding } from "../generated-models/PlannedQuotaPropertyBinding";
import { PlanRejectData } from "../generated-models/PlanRejectData";
import { IcmNotification } from "../generated-models/IcmNotification";
import { PlanRegionStatus } from "../generated-models/PlanRegionStatus";
import { EmailsPagination } from "../generated-models/EmailsPagination";
import { CommitPlan } from "../generated-models/CommitPlan";
import { CapacityOrder } from "../generated-models/CapacityOrder";
import { TeamConfig } from "../generated-models/TeamConfig";
import { BlueprintConfig } from "../generated-models/BlueprintConfig";
import { PlanDetail } from "../generated-models/PlanDetail";
import { VmDisk } from "../generated-models/VmDisk";
import { AssignPlanToRegion } from "../generated-models/AssignPlanToRegion";
import { PlansRisks } from "../generated-models/PlansRisks";
import { SubscriptionSupportInfo } from "../generated-models/SubscriptionSupportInfo";
import { BatchSku } from "../generated-models/BatchSku";
import { PlanRegionApproval } from "../generated-models/PlanRegionApproval";
import { FfpPlan } from "../generated-models/FfpPlan";
import { FfpPlanFile } from "../generated-models/FfpPlanFile";
import { Blueprint } from "../generated-models/Blueprint";
import { PslAssignmentProject, PslService } from "../capacity-blueprint/psl-assignment.model";
import { PfPlan } from "../generated-models/PfPlan";
import { PfPlanFile } from "../generated-models/PfPlanFile";
import { CommitPfPlan } from "../generated-models/CommitPfPlan";
import { PfRequest } from "../generated-models/PfRequest";
import { CreatePfRequest } from "../generated-models/CreatePfRequest";
import { CapacityOrderRefreshResult } from "../generated-models/CapacityOrderRefreshResult";
import { PfPlanRejectData } from "../generated-models/PfPlanRejectData";
import { FfpPlanSubmission } from "../generated-models/FfpPlanSubmission";
import { FfpPlanRejectData } from "../generated-models/FfpPlanRejectData";
import { Message } from "../generated-models/Message";
import { CapacitySubOrderDetails } from "../generated-models/CapacitySubOrderDetails";
import { CapacityOrderDetails } from "../generated-models/CapacityOrderDetails";
import { CcoTestOrderRequest, CcoTestOrderResult } from "../quota/admin-view/admin-view-cco-test.model";
import { Manatree } from "../generated-models/Manatree";
import { Project } from "../generated-models/Project";
import { ProjectBuildout } from "../generated-models/ProjectBuildout";
import { FfpPlanInstance } from "../generated-models/FfpPlanInstance";
import { FfpPlanInstanceApproval } from "../generated-models/FfpPlanInstanceApproval";
import { FfpPlanInstanceOverallApproval } from "../generated-models/FfpPlanInstanceOverallApproval";
import { FfpPlanInstanceDetail } from "../generated-models/FfpPlanInstanceDetail";
import { FfpAssignPlanToProject } from "../generated-models/FfpAssignPlanToProject";
import { PlanRegionWithApprovalStatus } from "../generated-models/PlanRegionWithApprovalStatus";
import { Alternative } from "../generated-models/Alternative";
import { UpdateAlternative } from "../generated-models/UpdateAlternative";
import { PlanDetailGroup } from "../generated-models/PlanDetailGroup";
import { PlanOnboard } from "../generated-models/PlanOnboard";
import { ChangePlanKind } from "../generated-models/ChangePlanKind";
import { Comment } from "../generated-models/Comment";
import { PfPlanDetail } from "../generated-models/PfPlanDetail";
import { PfSku } from "../generated-models/PfSku";
import { Demand } from "../generated-models/Demand";
import { DemandItem } from "../generated-models/DemandItem";
import { DemandItemAlternative } from "../generated-models/DemandItemAlternative";
import { DemandItemProperty } from "../generated-models/DemandItemProperty";
import { DemandItemExpanded } from "../generated-models/DemandItemExpanded";

@Injectable()
export class ApiService {
  private httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: "my-auth-token",
    }),
  };

  public static userProfile: IUserProfile = null;

  constructor(private http: HttpClient, private globalErrorHandler: GlobalErrorHandler) { }

  static createRequestRegionFromRegion(r: Region, val: number): IRequestRegion {
    return {
      value: val,
      label: r.RegionName,
      cloud: r.CloudName,
      regionSupportAz: !!r.AZFeatureFlag,
      isGA: r.Status === RegionStatus.GA,
      constraintNote: r.ConstraintNote || "",
      dcmtRegionId: r.DCMTRegionId,
      isManagedRegion: r.Status === RegionStatus.GA && !!r.RequiredFeatures,
      isEnabled: r.IsEnabled,
      isFlighting: r.IsFlighting,
      isEnabledInUI: r.IsEnabledInUI,
      isEnabledForCustomerEA: r.IsEnabledForCustomerEA,
      IsEnabledBetOnDemand: r.IsEnabledBetOnDemand,
      isManagedAZ: r.Status === RegionStatus.GA && !!r.AZFeatureFlag,
      IsCappedForCapacityOrder: r.IsCappedForCapacityOrder,
    } as IRequestRegion;
  }

  static generatePlannedQuotaRequestHyperLink(requestId: string): string {
    const href = `${window.location.origin}${environment.siteRootPath}/plans/requests/${requestId}`;
    return `<a href="${href}" rel="noopener noreferrer" target="_blank" title="${requestId}">${requestId}</a>`;
  }

  static generateSubRequestHyperLink(parentRequestId: string, subRequestId: string): string {
    const href = `${window.location.origin}${environment.siteRootPath}/requests/${parentRequestId}/sub/${subRequestId}`;
    return `<a href="${href}" rel="noopener noreferrer" target="_blank" data-test="SubRequestLink" title="${parentRequestId}-${subRequestId}">${parentRequestId}-${subRequestId}</a>`;
  }

  static generateParentRequestHyperLink(parentRequestId: string, text = ""): string {
    let href = `${window.location.origin}${environment.siteRootPath}`;
    // Only Customer Request Id contains '_'
    if (parentRequestId.indexOf("_") > 0) {
      const [billingAccountId, region] = parentRequestId.split("_");
      href += `/customer-request/customers/${billingAccountId}/regions/${region}`;
    } else {
      href += `/requests/${parentRequestId}`;
    }

    // Default text is parent request id
    if (!text) {
      text = parentRequestId;
    }

    return `<a href="${href}" rel="noopener noreferrer" target="_blank" title="${parentRequestId}">${text}</a>`;
  }

  static generateCapacityOrderHyperLink(capacityOrderId: string): string {
    const href = `${window.location.origin}${environment.siteRootPath}/plans/capacityOrder/${capacityOrderId}`;
    return `<a href="${href}" rel="noopener noreferrer" target="_blank" title="${capacityOrderId}">${capacityOrderId}</a>`;
  }

  static fetchServiceTypeName(dbServiceType: string, sku: string): string {
    const serviceTypeMapping = {
      AZ: "AZ Enablement",
      CAS: "Compute - Region Enablement CAS",
      SQL: "SQL DB - Region Enablement",
      SQLMI: "SQL MI - Region Enablement",
      HDInsight: "HDInsight - Quota Increase",
      ARM: "ARM - Region Enablement",
      RDFE: "RDFE - Region Enablement",
      ArmVmQuota: "Compute ARM - Quota Increase",
      RdfeVmQuota: "Compute RDFE - Quota Increase",
      ArmStorageQuota: "Storage ARM - Quota Increase",
      RdfeStorageQuota: "Storage RDFE - Quota Increase",
      DNSEntry: "DNSEntry - Quota Increase",
      BatchQuota: "Batch - Quota Increase",
      CosmosDB: "Cosmos DB - Quota Increase",
      KustoQuota: "Kusto - Quota Increase",
      InternalVm: "VM SKU AFEC",
      RPFrontload: "RP Frontload - Region Enablement",
      ArmNoQuota: "ARM No Quota - Region Enablement",
      AppService: "App Service - Quota Increase",
      Kusto: "Kusto Enablement",
      ArmResourceType: "ARM Resource Type",
      VmDisk: "VM Disk - Quota Increase",
      GroupQuota: "Group Quota Increase"
    };

    // Override by SKU
    switch (dbServiceType) {
      case RequestServiceType.CosmosDB:
        if (sku === "RegionEnable") {
          return "Cosmos DB - Region Enablement";
        } else if (sku === "Az Enablement") {
          return "Cosmos DB - AZ Enablement";
        } else if (sku == "Access") {
          return "Cosmos DB - Region Access";
        }
        return "Cosmos DB - Quota Increase";
      case RequestServiceType.SQL:
        if (sku === "DTU") {
          return "SQL DTU - Quota Increase";
        }
        return "SQL DB - Region Enablement";
      case RequestServiceType.HDInsight:
        if (sku === "Access") {
          return "HDInsight";
        }
        break;
      case RequestServiceType.BatchQuota:
        if (sku === "Access") {
          return "Batch";
        }
        break;
      case RequestServiceType.AppService:
        if (sku === "Access") {
          return "App Service";
        }
        break;
    }

    if (dbServiceType in serviceTypeMapping) {
      return serviceTypeMapping[dbServiceType];
    }
  }

  async getUserProfile(): Promise<IUserProfile> {
    if (!ApiService.userProfile) {
      try {
        const [response] = await combineLatest([this.http.get(NprConfiguration.getProfileUrl, this.httpOptions)]).toPromise();
        ApiService.userProfile = response as IUserProfile;
        const roles = ApiService.userProfile.UserRoles;
        if (roles?.some((role) => role.Role === Roles.Administrator)) {
          ApiService.userProfile.IsAdmin = true;
        }
        if (ApiService.userProfile.IsAdmin || roles?.some((role) => role.Role === Roles.Operator)) {
          ApiService.userProfile.IsOperator = true;
        }
        if (ApiService.userProfile.IsAdmin || roles?.some((role) => role.Role === Roles.FfpAdmin)) {
          ApiService.userProfile.IsFfpAdmin = true;
        }

        if (ApiService.userProfile.IsAdmin || roles?.some((role) => role.Role === Roles.PfAdmin)) {
          ApiService.userProfile.IsPfAdmin = true;
          ApiService.userProfile.IsApprover = true;
        }
        if (ApiService.userProfile.IsAdmin || roles?.some((role) => role.Role === Roles.ApproverAdmin)) {
          ApiService.userProfile.IsApproverAdmin = true;
        }
        if (ApiService.userProfile.IsApproverAdmin || roles?.some((role) => role.Role === Roles.Approver)) {
          ApiService.userProfile.IsApprover = true;
        }
        if (ApiService.userProfile.IsApprover || roles?.some((role) => role.Types.includes("GCT"))) {
          ApiService.userProfile.IsGCTApprover = true;
        }

        if (
          roles?.some(
            (role) => role.Role == Roles.BetServiceOwner || role.Role == LionrockUserRoles.RolePrefix + LionrockUserRoles.Administrator
          )
        ) {
          ApiService.userProfile.IsBetServiceOwner = true;
        }

        if (roles?.some((role) => role.Role == Roles.BetPslLaunchLeader)) {
          ApiService.userProfile.IsBetPslLaunchLeader = true;
        }
      } catch (error) {
        await this.handleError(error);
      }
    }
    return ApiService.userProfile;
  }

  setApprovalLevel(parentRequestId: string, subRequestId: number, approvalData: IApprovalData): Observable<IStatusResponse[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests/${subRequestId}/approve`;
    return this.http.post<IStatusResponse[]>(url, approvalData, this.httpOptions).pipe(catchError(this.handleError));
  }

  batchSetApprovalLevel(batchApprovalData: Array<SubRequestBatchApproval>): Observable<IStatusResponse[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/batchapprove`;
    return this.http.post<IStatusResponse[]>(url, batchApprovalData, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateSubRequest(parentRequestId: string, subRequestId: number, updateData: ISubRequestUpdateData): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests/${subRequestId}`;
    return this.http.post<IStatusResponse>(url, updateData, this.httpOptions).pipe(catchError(this.handleError));
  }

  async GetSubRequest(parentRequestId: string, subRequestId: number): Promise<ISubRequest> {
    try {
      const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests/${subRequestId}`;
      const response = await this.http.get(url, this.httpOptions).toPromise();
      return response as ISubRequest;
    } catch (error) {
      await this.handleError(error);
    }
  }

  getSubRequestsOperationHistory(parentRequestId: string, subRequestId: number): Observable<OperationLog[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests/${subRequestId}/history`;
    return this.http.get<OperationLog[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDiscussions(parentRequestId: string, subRequestId: number): Observable<Discussion[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests/${subRequestId}/discussion`;
    return this.http.get<Discussion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  appendDiscussion(parentRequestId: string, subRequestId: number, discussion: Discussion) {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests/${subRequestId}/discussion`;
    return this.http.post(url, discussion, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubRequestsByParentRequestId(parentRequestId: string): Observable<ISubRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests`;
    return this.http.get<ISubRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDuplicatedSubRequestBySubRequestId(parentReqId: string, subRequestId: number): Observable<ISubRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentReqId}/subrequests/${subRequestId}/duplicates`;
    return this.http.get<ISubRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubRequestApprovals(parentRequestId: string, subRequestId: number): Observable<ApprovalList> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/subrequests/${subRequestId}/approval`;
    return this.http.get<ApprovalList>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubscriptionZoneInfo(request: ISubscriptionInfoInput, cloud: string): Observable<SubscriptionZoneInfo[]> {
    const url = `${NprConfiguration.getSubscriptionInfoReqUrl}/subscriptionZoneInfo?cloud=${cloud}`;
    return this.http.post<SubscriptionZoneInfo[]>(url, request, this.httpOptions).pipe(catchError(this.handleError));
  }

  revokeAccessOrQuota(request: ISelfServiceInfoInput): Observable<IStatusResponse> {
    const url = `${NprConfiguration.selfServiceUrl}/revoke`;
    return this.http.post<IStatusResponse>(url, request, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubscriptionDetails(subscriptionId: string): Observable<ISubscriptionDetails> {
    const url = `${NprConfiguration.getSubscriptionInfoReqUrl}/${subscriptionId}/subscriptionDetails`;
    return this.http.get<ISubscriptionDetails>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getQuotaDetails(subscriptionId: string, regionName: string): Observable<string> {
    const url = `${NprConfiguration.getSubscriptionInfoReqUrl}/${subscriptionId}/quotaDetails?region=${regionName}`;
    return this.http.get<string>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getParentRequest(requestId: string): Observable<IParentRequest> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${requestId}`;
    return this.http.get<IParentRequest>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubRequestByUser(): Observable<ISubRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/subrequests/mine`;
    return this.http.get<ISubRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getParentRequestAndWorkItemByUser(incompleteWorkItems: boolean): Observable<MyRequestAndWorkItems> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/mine?incompleteWorkItems=${incompleteWorkItems}`;
    return this.http.get<MyRequestAndWorkItems>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getTickets(requestId: string): Observable<Ticket[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/${requestId}/tickets`;
    return this.http.get<Ticket[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubRequestsWithTicketsInfo(searchRequests: SearchRequests): Observable<ISubRequestWithTicket[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/withTicketsInfo`;
    return this.http.post<ISubRequestWithTicket[]>(url, searchRequests, this.httpOptions).pipe(catchError(this.handleError));
  }

  getCisJobByRequestId(requestId: string): Observable<CisJob> {
    const url = `${NprConfiguration.getGenericReqUrl}/${requestId}/cisjob`;
    return this.http.get<CisJob>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  setSubRequestTags(requestId: string, tags: string): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${requestId}/tags`;
    return this.http.post<IStatusResponse>(url, JSON.stringify(tags), this.httpOptions).pipe(catchError(this.handleError));
  }

  getWorkItemById(ticketId: string, region: string): Observable<IWorkItemResponse> {
    const url = `${NprConfiguration.getWorkItemReqUrl}/${ticketId}?regionName=${region}`;
    return this.http.get<IWorkItemResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getIncompleteWorkItems(region: string): Observable<IWorkItemResponse[]> {
    let url = `${NprConfiguration.getWorkItemReqUrl}/status/incomplete`;
    if (region) {
      url += `?selectedRegion=${region}`;
    }
    return this.http.get<IWorkItemResponse[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateWorkItemById(ticketId: string, region: string, commentUpdate: CommentUpdate): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getWorkItemReqUrl}/${ticketId}/comments?regionName=${region}`;
    return this.http.post<IStatusResponse>(url, commentUpdate, this.httpOptions).pipe(catchError(this.handleError));
  }

  async WorkItemsBulkUpdate(workitemList: IWorkItemResponse[], comment: string) {
    const results = [] as Observable<IStatusResponse>[];
    workitemList.map((workitem) => {
      try {
        const commentUpdate = {} as CommentUpdate;
        commentUpdate.Comment = comment;
        this.updateWorkItemById(workitem.Id, workitem.ArmRegionName, commentUpdate)
          .toPromise()
          .then(() => {
            console.log(`workitem: ${workitem.Id} in region ${workitem.ArmRegionName} is updated.`);
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (error) {
        this.handleError(error);
      }
    });
    return results;
  }

  getErrorParentRequests(): Observable<IParentRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/status/error`;
    return this.http.get<IParentRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAllParentRequests(includeArchived = false): Observable<IParentRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests?includeArchived=${includeArchived}`;
    return this.http.get<IParentRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  retryParentRequests(requestId: string, forceRetry: boolean): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${requestId}/retry?forceRetry=${forceRetry}`;
    return this.http.post<IStatusResponse>(url, null, this.httpOptions).pipe(catchError(this.handleError));
  }

  cancelParentRequests(requestId: string): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${requestId}/cancel`;
    return this.http.post<IStatusResponse>(url, null, this.httpOptions).pipe(catchError(this.handleError));
  }

  skipDependenciesOfParentRequests(requestId: string): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${requestId}/skipdependencies`;
    return this.http.post<IStatusResponse>(url, null, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAllSubRequests(showArchived = false): Observable<ISubRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/subrequests?includeArchived=${showArchived}`;
    return this.http.get<ISubRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPendingApprovalRequests(): Observable<ISubRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/subrequests/pendingapproval`;
    return this.http.get<ISubRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getErrorSubRequests(): Observable<ISubRequest[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/subrequests/status/error`;
    return this.http.get<ISubRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  retrySubRequests(requestId: string): Observable<IStatusResponse> {
    const req = ParseRequestId(requestId);
    const url = `${environment.lionrockApiEndpoint}/api/requests/${req.parentRequestId}/subrequests/${req.subRequestId}/retry`;
    return this.http.post<IStatusResponse>(url, JSON.stringify(requestId), this.httpOptions).pipe(catchError(this.handleError));
  }

  skipDependenciesOfSubRequests(requestId: string): Observable<IStatusResponse> {
    const req = ParseRequestId(requestId);
    const url = `${environment.lionrockApiEndpoint}/api/requests/${req.parentRequestId}/subrequests/${req.subRequestId}/skipdependencies`;
    return this.http.post<IStatusResponse>(url, JSON.stringify(requestId), this.httpOptions).pipe(catchError(this.handleError));
  }

  cancelSubRequests(requestId: string, comment: string, sendEmail: boolean): Observable<string> {
    const req = ParseRequestId(requestId);
    const url = `${environment.lionrockApiEndpoint}/api/requests/${req.parentRequestId}/subrequests/${req.subRequestId}/cancel?sendEmail=${sendEmail}`;
    return this.http.post<string>(url, JSON.stringify(comment), this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionList(includeDisabled = false, includeAirGapped = true): Observable<Region[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions?includeDisabled=${includeDisabled}&includeAirGapped=${includeAirGapped}`;
    return this.http.get<Region[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getEdgeZoneList(): Observable<EdgeZone[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/edgezones`;
    return this.http.get<EdgeZone[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionSupportInfo(): Observable<RegionSupportInfo[]> {
    const url = `${NprConfiguration.selfServiceUrl}/info/ARM/regions`;
    return this.http.get<RegionSupportInfo[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getARMComputeSubscriptionSupportInfo(subscriptionId: string, region: string): Observable<SubscriptionSupportInfoARM> {
    const url = `${NprConfiguration.selfServiceUrl}/info/ARM/regions/${region}/services/compute/subscriptions/${subscriptionId}`;
    return this.http.get<SubscriptionSupportInfoARM>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubscriptionSupportInfo(subscriptionId: string, region: string): Observable<SubscriptionSupportInfo> {
    const url = `${NprConfiguration.selfServiceUrl}/info/ARM/regions/${region}/subscriptions/${subscriptionId}`;
    return this.http.get<SubscriptionSupportInfo>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getARMStorageSubscriptionSupportInfo(subscriptionId: string, region: string): Observable<SubscriptionRegionSupportInfo> {
    const url = `${NprConfiguration.selfServiceUrl}/info/ARM/regions/${region}/services/storage/subscriptions/${subscriptionId}`;
    return this.http.get<SubscriptionRegionSupportInfo>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getArmResourceTypeSubscriptionSupportInfo(subscriptionId: string, region: string): Observable<string[]> {
    const url = `${NprConfiguration.selfServiceUrl}/info/ARM/regions/${region}/services/armresourcetype/subscriptions/${subscriptionId}`;
    return this.http.get<SubscriptionRegionSupportInfo>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getVmDiskSubscriptionSupportInfo(subscriptionId: string, region: string): Observable<VmDisk[]> {
    const url = `${NprConfiguration.selfServiceUrl}/info/ARM/regions/${region}/services/vmdisk/subscriptions/${subscriptionId}`;
    return this.http.get<VmDisk[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRestrictedRegionList(): Observable<Region[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/restricted`;
    return this.http.get<Region[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRestrictedRegionListForCosmosDB(): Observable<string[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/restricted/cosmosDB`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionConfig(): Observable<RegionConfig[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/config`;
    return this.http.get<RegionConfig[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getTeamConfig(): Observable<TeamConfig[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/teams/config`;
    return this.http.get<TeamConfig[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDefaultTeamConfig(): Observable<TeamConfig> {
    const url = `${NprConfiguration.getGenericReqUrl}/teams/config/default`;
    return this.http.get<TeamConfig>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  setTeamConfig(data: TeamConfig): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/teams/config`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteTeamConfig(oid: string, region: string): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/teams/${oid}/regions/${region}/config`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getBlueprintConfig(): Observable<BlueprintConfig[]> {
    const url = `${NprConfiguration.baseApiUrl}/blueprint/config`;
    return this.http.get<BlueprintConfig[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  setBlueprintConfig(data: BlueprintConfig): Observable<IStatusResponse> {
    const url = `${NprConfiguration.baseApiUrl}/blueprint/config`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteBlueprintConfig(serviceTreeId: string, blueprint: string, region: string): Observable<IStatusResponse> {
    const url = `${NprConfiguration.baseApiUrl}/blueprint/config/${serviceTreeId}/${blueprint}/${region}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getEdgeZoneConfig(): Observable<EdgeZoneConfig[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/edgezones/config`;
    return this.http.get<EdgeZoneConfig[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDefaultRegionConfig(): Observable<RegionConfig> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/config/default`;
    return this.http.get<RegionConfig>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  setRegionConfig(data: RegionConfig): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/config`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  setEdgeZoneConfig(data: EdgeZoneConfig): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/edgezones/config`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAzEntity(includeAll: boolean): Observable<AZ[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/azentity?includeAll=${includeAll}`;
    return this.http.get<AZ[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateAzEntity(data: AZ): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/azentity`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  createAzEntity(data: AZ): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/azentity`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteAzEntity(data: AZ): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/azentity/${encodeURIComponent(data.AvailabilityZoneName)}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAZConfig(): Observable<AZConfig[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/azconfig`;
    return this.http.get<AZConfig[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  setAZConfig(data: AZConfig): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/azconfig`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteRegionConfig(data: RegionConfig): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/regions/${data.RegionName}/config`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteEdgeZoneConfig(data: EdgeZoneConfig): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/edgezones/${data.EdgeZoneId}/config`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getUserRoleList(): Observable<UserRole[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/userroles`;
    return this.http.get<UserRole[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addUserRole(data: UserRole): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/userroles`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  setUserRole(data: UserRole): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/userroles`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteUserRole(data: UserRole): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/userroles/upn/${data.Upn}/role/${data.Role}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addTeamAttest(serviceTreeId: string, cloudName: string, requestSource: string): Observable<IStatusResponse> {
    const url = `${NprConfiguration.selfServiceUrl}/odqattest/services/${serviceTreeId}/channels/${requestSource}/clouds/${cloudName}`;
    return this.http.post<IStatusResponse>(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }


  getApproverRoles(): Observable<string[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/approverroles`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getApproverRoleTypes(): Observable<string[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/approverroletypes`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getApprovers(): Observable<UserRole[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/approvers`;
    return this.http.get<UserRole[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addApprovers(data: UserRole): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/approvers`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  setApprovers(data: UserRole): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/approvers`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteApprovers(data: UserRole[]): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/approvers/delete`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  getApproversByTypeAndRegion(type: string, region: string): Observable<string[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/approvers/types/${type}/regions/${region}`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAutoApprovalRuleList(): Observable<AutoApprovalRule[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules`;
    return this.http.get<AutoApprovalRule[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getHoboAutoApprovalRuleList(): Observable<HoboSubscriptionAutoApprovalRule[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules-hobo`;
    return this.http.get<HoboSubscriptionAutoApprovalRule[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateAutoApprovalRule(data: AutoApprovalRule): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  insertAutoApprovalRule(data: AutoApprovalRule): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteAutoApprovalRule(id: number): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules/id/${id}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAutoApprovalThresholdList(): Observable<AutoApprovalThreshold[]> {
    const url = `${NprConfiguration.baseApiUrl}/autoapprovalsthresholds`;
    return this.http.get<AutoApprovalThreshold[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateAutoApprovalThreshold(data: AutoApprovalThreshold): Observable<IStatusResponse> {
    const url = `${NprConfiguration.baseApiUrl}/autoapprovalsthresholds/${data.Location}/${data.RequestServiceType}/${data.Sku}`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  insertAutoApprovalThreshold(data: AutoApprovalThreshold): Observable<IStatusResponse> {
    const url = `${NprConfiguration.baseApiUrl}/autoapprovalsthresholds`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteAutoApprovalThreshold(location: string, requestServiceType: string, sku: string): Observable<IStatusResponse> {
    const url = `${NprConfiguration.baseApiUrl}/autoapprovalsthresholds/${location}/${requestServiceType}/${sku}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateHoboAutoApprovalRule(data: HoboSubscriptionAutoApprovalRule): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules-hobo`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  insertHoboAutoApprovalRule(data: HoboSubscriptionAutoApprovalRule): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules-hobo`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteHoboAutoApprovalRule(id: number): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/autoapprovalrules-hobo/id/${id}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSubscriptionThresholdList(): Observable<SubscriptionThreshold[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/subCount`;
    return this.http.get<SubscriptionThreshold[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateSubscriptionThreshold(data: SubscriptionThreshold): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/subCount`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  insertSubscriptionThreshold(data: SubscriptionThreshold): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/subCount`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteSubscriptionThreshold(id: number): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/subCount/id/${id}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalQuotaLimitList(): Observable<RegionalQuotaLimit[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/regionalQuota`;
    return this.http.get<RegionalQuotaLimit[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateRegionalQuotaLimit(data: RegionalQuotaLimit): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/regionalQuota`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  insertRegionalQuotaLimit(data: RegionalQuotaLimit): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/regionalQuota`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteRegionalQuotaLimit(id: number): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/regionalQuota/${id}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getServiceHandlingList(): Observable<Config[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceHandling`;
    return this.http.get<Config[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateServiceHandling(data: Config): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceHandling`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  insertServiceHandling(data: Config): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceHandling`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteServiceHandling(id: number): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceHandling/id/${id}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getVmSkus(): Observable<VmSku[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/vmSkus`;
    return this.http.get<VmSku[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAllVmSkuEntities(): Observable<VmSku[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/VmSkuEntities`;
    return this.http.get<VmSku[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAllPfSkuEntities(): Observable<PfSku[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/PfSkuEntities`;
    return this.http.get<PfSku[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updatePfSku(data: PfSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/PfSkuEntities`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  createPfSku(data: PfSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/PfSkuEntities`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deletePfSku(data: PfSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/PfSkuEntities/${encodeURIComponent(data.Sku)}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateVmSku(data: VmSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/VmSkuEntities`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  createVmSku(data: VmSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/VmSkuEntities`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteVmSku(data: VmSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/VmSkuEntities/${data.RegionName}/${data.CrpVmSku}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getVmSkusByRegion(region?: string): Observable<VmSku[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/vmSkus/availability?region=${region || ""}`;
    return this.http.get<VmSku[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getVmSizes(): Observable<VmSize[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/vmSizes`;
    return this.http.get<VmSize[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getHdiSkus(): Observable<HdiSku[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/hdi/skus`;
    return this.http.get<HdiSku[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  createHdiSku(data: HdiSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/hdi/skus`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteHdiSku(data: HdiSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/hdi/skus/${encodeURIComponent(data.RegionName)}/${encodeURIComponent(data.CrpVmSku)}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getBatchSkus(): Observable<BatchSku[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/batch/skus`;
    return this.http.get<BatchSku[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  createBatchSku(data: BatchSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/batch/skus`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteBatchSku(data: BatchSku): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/batch/skus/${encodeURIComponent(data.RegionName)}/${encodeURIComponent(data.CrpVmSku)}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAppServiceSkus(): Observable<string[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/appService/skus`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getArmResourceTypeByRegion(region: string): Observable<ArmResourceType[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/armResourceType/regions/${region}`;
    return this.http.get<ArmResourceType[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getVmDiskByRegion(region: string): Observable<VmDisk[]> {
    const url = `${NprConfiguration.baseApiUrl}/vmDisk/regions/${region}`;
    return this.http.get<VmDisk[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getVmDisk(): Observable<VmDisk[]> {
    const url = `${NprConfiguration.baseApiUrl}/vmDisk`;
    return this.http.get<VmDisk[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getTeamList(): Observable<Team[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/teams`;
    return this.http.get<Team[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getEmailsColumnSelection(columnName: string): Observable<string[]> {
    const url = `${NprConfiguration.baseApiUrl}/emails/columns/${columnName}`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getEmailsPagination(startRow: number, endRow: number, sortModel: any, filterModel: any): Observable<EmailsPagination> {
    const url = `${NprConfiguration.baseApiUrl}/emails/pagination`;
    const body = {
      StartRow: startRow,
      EndRow: endRow,
      SortModels: sortModel,
      FilterModels: filterModel,
    };
    return this.http.post<EmailsPagination>(url, body, this.httpOptions).pipe(catchError(this.handleError));
  }

  getServiceStatus(region: string, isFlighting: boolean): Observable<{ [key: string]: string }> {
    const url = `${NprConfiguration.baseApiUrl}/regions/${region}/serviceStatus?isFlighting=${isFlighting}`;
    const response = this.http.get<{ [key: string]: string }>(url, this.httpOptions);
    return response;
  }

  createRequest(request: IUIRequest): Observable<string> {
    return this.http.post<string>(NprConfiguration.newRequestUrl, request, this.httpOptions).pipe(catchError(this.handleError));
  }

  validateRequest(request: IUIRequest, screenNumber: number): Observable<string> {
    const url = `${NprConfiguration.newRequestUrl}/validate${screenNumber === 1 ? "/basicInfo" : ""}`;
    const response = this.http.post<string>(url, request, this.httpOptions).pipe(catchError(this.handleError));
    return response;
  }

  checkParentRequestDuplicates(request: IUIRequest): Observable<IParentRequest[]> {
    return this.http
      .post<IParentRequest[]>(NprConfiguration.newRequestUrl + "/duplicates/parent-request", request, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  getAllCapacityOrders(): Observable<CapacityOrder[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/capacity-order`;
    return this.http.get<CapacityOrder[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  refreshCapacityOrder(id: string): Observable<CapacityOrderDetails> {
    const url = `${NprConfiguration.baseApiUrl}/capacityOrders/${id}/refresh`;
    return this.http.post<CapacityOrderDetails>(url, JSON.stringify(id), this.httpOptions).pipe(catchError(this.handleError));
  }

  createCcoTestOrder(request: CcoTestOrderRequest): Observable<CcoTestOrderResult> {
    const url = `${NprConfiguration.baseApiUrl}/admin/cco-test/orders`;
    return this.http.post<CcoTestOrderResult>(url, request, this.httpOptions).pipe(catchError(this.handleError));
  }

  getCcoTestOrder(orderId: string): Observable<CapacityOrderDetails> {
    const url = `${NprConfiguration.baseApiUrl}/admin/cco-test/orders/${encodeURIComponent(orderId)}`;
    return this.http.get<CapacityOrderDetails>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteCcoTestOrder(orderId: string): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/admin/cco-test/orders/${encodeURIComponent(orderId)}`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRdQuotaQueryUrl(id: string): Observable<string> {
    const url = `${NprConfiguration.baseApiUrl}/capacityOrders/${id}/rdQuotaQueryUrl`;
    return this.http.get(url, { ...this.httpOptions, responseType: "text" });
  }

  getServiceTreeIdsByPsl(psl: string): Observable<string[]> {
    const url = `${NprConfiguration.baseApiUrl}/psl/${psl}`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  // Use arrow function so this context is not lost
  private handleError = (error: HttpErrorResponse): Observable<any> => {
    let errorMessage = "";
    if (error.error instanceof ErrorEvent) {
      errorMessage = `A client-side or network error occurred: ${error.error.message}`;
    } else {
      if (error.status === 0) {
        errorMessage = "AJAX request was cancelled. ";
        return of(null);
      }
      // The backend returned an unsuccessful response code.
      else if (error.status === 404) {
        errorMessage = "Resource not found. ";
        // TODO: handle 404 globally
      } else if (error.status === 403) {
        errorMessage = "Access denied. ";
        // TODO: handle 404 globally
      }

      // The response body may contain clues as to what went wrong.
      if (error.error?.message) {
        // process errors with expected structure. All error response from Lionrock should contain a "message" field (and a "code" field).
        errorMessage += `Server response: ${error.error.message}`;
      } else if (error.error) {
        if (typeof error.error === "object") {
          errorMessage += `Server response: ${JSON.stringify(error.error)}`;
        } else {
          errorMessage += `Server response: ${error.error}`;
        }
      } else if (error.message) {
        /**
         * process errors with unexpected structure.
         * Maybe the error response is generated by a middleware rather than the code of Lionrock.
         * We should try to handle such cases on the backend, and here is just a fallback.
         */
        errorMessage += `Server response: ${error.message}`;
      }
    }

    // Return an observable with a user-facing error message.
    console.error(errorMessage);
    return throwError(() => errorMessage);
  };

  getQuotaImplications(team: string, region: string): Observable<IQuotaImplicationsData[]> {
    const url = `${NprConfiguration.getQuotaImplicationsUrl}?teamOid=${team}&region=${region}`;
    return this.http.get<IQuotaImplicationsData[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  setAdminUiGeneralInfo(data: any, apiString: string): Observable<void> {
    const url = `${NprConfiguration.setAdminUiInfo}/${apiString}`;
    const payload = data;
    return this.http.put<void>(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyPlans(): Observable<Plan[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/mine`;
    return this.http.get<Plan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlans(): Observable<Plan[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}`;
    return this.http.get<Plan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlan(serviceTreeId: string, name: string): Observable<Plan> {
    var url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}`;
    return this.http.get<Plan>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlansHistory(serviceTreeId: string, name: string): Observable<Plan[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/history`;
    return this.http.get<Plan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  assignPlanToRegion(payload: PlanSubmission, ignoreVmSkusNotInPslWarning: boolean = false) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/assign?ignoreVmSkusNotInPslWarning=${ignoreVmSkusNotInPslWarning}`;
    return this.http.post<AssignPlanToRegion>(url, payload, this.httpOptions);
  }

  assignPlanPartiallyFulfilledReconfirmed(payload: PlanSubmission) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/assign?isPartiallyFulfillmentReconfirmed=true`;
    return this.http.post(url, payload, this.httpOptions);
  }

  requeuePlanRegion(payload: PlanSubmission) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/requeue`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  requeueExpiredPlanRegion(payload: PlanSubmission) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/requeue-expired`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  signOffPlanRegions(payload: PlanSubmission[]) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/signoff`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  approvePlanRegion(serviceTreeId: string, region: string, planRegionOverallApproval: PlanRegionOverallApproval): Observable<OkMessage> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/approve`;
    return this.http.post<OkMessage>(url, planRegionOverallApproval, this.httpOptions).pipe(catchError(this.handleError));
  }

  syncExecutionPlan(): Observable<OkMessage> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/syncExecutionPlan`;
    return this.http.post<OkMessage>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  rejectPlanRegion(serviceTreeId: string, region: string, planRejectData: PlanRejectData) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/reject`;
    return this.http.post(url, planRejectData, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanRegion(serviceTreeId: string, region: string, version: number, blueprint: string): Observable<PlanRegion> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}`;
    return this.http.get<PlanRegion>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanRegionLatestVersion(serviceTreeId: string, region: string, blueprint: string): Observable<PlanRegion> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/blueprints/${blueprint}/latest`;
    return this.http.get<PlanRegion>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanRegionDetail(serviceTreeId: string, region: string, version: number, blueprint: string): Observable<PlanRegionDetail> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/detail`;
    return this.http.get<PlanRegionDetail>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanDetailGroup(serviceTreeId: string, region: string, version: number, blueprint: string): Observable<PlanDetailGroup[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/detailGroup`;
    return this.http.get<PlanDetailGroup[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateValidToDate(serviceTreeId: string, region: string, version: number, blueprint: string, updateData: PlanRegionUpdateData) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}`;
    return this.http.put(url, updateData, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalPlans(status?: PlanRegionStatusEnum, hasApprovalStatus: boolean = false): Observable<PlanRegionWithApprovalStatus[]> {
    let url = `${PlannedQuotaConfiguration.plansUrl}/regional`;
    if (status) {
      url = url + `?status=${status}`;
    }
    if (hasApprovalStatus) {
      url = url + (status ? "&" : "?") + "hasApprovalStatus=true";
    }
    return this.http.get<PlanRegionWithApprovalStatus[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalPlansByServiceTreeId(serviceTreeId: string): Observable<PlanRegion[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regional`;
    return this.http.get<PlanRegion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getApprovableRegionalPlans(showApprovableOnly: boolean): Observable<PlanRegionWithApprovalStatus[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/regional/approvable?showApprovableOnly=${showApprovableOnly}`;
    return this.http.get<PlanRegionWithApprovalStatus[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getLatestRegionalPlans(serviceTreeId: string): Observable<PlanRegion[]> {
    var url = `${PlannedQuotaConfiguration.plansUrl}/regional/latest`;
    if (serviceTreeId) {
      url += `?serviceTreeId=${serviceTreeId}`;
    }
    return this.http.get<PlanRegion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalPlanDiff(serviceTreeId: string, region: string, version: number, blueprint: string): Observable<PlanRegionDiff[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/diff`;
    return this.http.get<PlanRegionDiff[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalPlanRelatedCapacityOrders(serviceTreeId: string, region: string, version: number, blueprint: string): Observable<CapacityOrder[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/capacity-order`;
    return this.http.get<CapacityOrder[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalPlanApprovals(serviceTreeId: string, region: string, version: number, blueprint: string): Observable<PlanRegionApproval[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/approvals`;
    return this.http.get<PlanRegionApproval[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalPlanPendingApprovalTypes(serviceTreeId: string, region: string, version: number, blueprint: string): Observable<string[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/pendingApprovalTypes`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalQuotaStatistics(region: string): Observable<RegionalQuotaStatistics[]> {
    const url = `${environment.lionrockApiEndpoint}/api/plans/quota-statistics/regions/${region}`;
    return this.http.get<RegionalQuotaStatistics[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyRegionalPlans(): Observable<PlanRegion[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/regional/mine`;
    return this.http.get<PlanRegion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyServiceTeamServiceTreeIds(): Observable<string[]> {
    const url = `${environment.lionrockApiEndpoint}/api/services/mine`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyServiceTeamUser(): Observable<User[]> {
    const url = `${environment.lionrockApiEndpoint}/api/services/mine/users`;
    return this.http.get<User[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addMyServiceTeamUser(serviceTreeId: string, user: User[]): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/services/${serviceTreeId}/users`;
    return this.http.post<IStatusResponse>(url, user, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteMyServiceTeamUser(serviceTreeId: string, upn: string): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/services/${serviceTreeId}/users/${upn}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyServiceTeamApp(): Observable<App[]> {
    const url = `${environment.lionrockApiEndpoint}/api/services/mine/apps`;
    return this.http.get<App[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addMyServiceTeamApp(app: NewApp): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/services/${app.ServiceTreeId}/apps`;
    return this.http.post<IStatusResponse>(url, app, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteMyServiceTeamApp(serviceTreeId: string, appId: string): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/services/${serviceTreeId}/apps/${appId}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyTrustedServices(): Observable<TrustedService[]> {
    const url = `${environment.lionrockApiEndpoint}/api/services/mine/trusted-services`;
    return this.http.get<TrustedService[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addMyTrustedService(trustedService: NewTrustedService): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/services/${trustedService.ServiceTreeId}/trusted-services`;
    return this.http.post<IStatusResponse>(url, trustedService, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteMyTrustedService(serviceTreeId: string, trustedServiceTreeId: string): Observable<IStatusResponse> {
    const url = `${environment.lionrockApiEndpoint}/api/services/${serviceTreeId}/trusted-services/${trustedServiceTreeId}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaRequests(region: string, subscriptionId: string, serviceTreeId: string): Observable<PlannedQuotaRequest[]> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests?region=${region}&subscriptionId=${subscriptionId}&serviceTreeId=${serviceTreeId}`;
    return this.http.get<PlannedQuotaRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaRequestById(requestId: string): Observable<PlannedQuotaRequestDetail> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests/${requestId}`;
    return this.http.get<PlannedQuotaRequestDetail>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaRequestHistoryById(requestId: string): Observable<OperationLog[]> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests/${requestId}/history`;
    return this.http.get<OperationLog[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  createPlannedQuotaRequest(request: PlannedQuotaRequest) {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests`;
    return this.http.post<PlannedQuotaRequest[]>(url, request, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaRequestsPagination(
    startRow: number,
    endRow: number,
    sortModel: any,
    filterModel: any
  ): Observable<PlannedQuotaRequestPagination> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests/pagination`;
    const body = {
      StartRow: startRow,
      EndRow: endRow,
      SortModels: sortModel,
      FilterModels: filterModel,
    };
    return this.http.post<PlannedQuotaRequestPagination>(url, body, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaRequestsColumnSelection(columnName: string): Observable<string[]> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests/columns/${columnName}`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyPlannedQuotaRequests(): Observable<PlannedQuotaRequest[]> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests/mine`;
    return this.http.get<PlannedQuotaRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyServicesPlannedQuotaRequests(): Observable<PlannedQuotaRequest[]> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests/services/mine`;
    return this.http.get<PlannedQuotaRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  retryPlannedQuotaRequest(requestId: string): Observable<IStatusResponse> {
    const url = `${PlannedQuotaConfiguration.baseApiUrl}/requests/${requestId}/retry`;
    return this.http.post<IStatusResponse>(url, JSON.stringify(requestId), this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanFiles(serviceTreeId: string, name: string, version: number): Observable<PlanFile[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/files/versions/${version}`;
    return this.http.get<PlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getLatestPlanFiles(serviceTreeId: string, name: string): Observable<PlanFile[]> {
    const  url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/files/latest`;
    return this.http.get<PlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanFileTemplate(serviceTreeId: string, name: string): Observable<PlanFile> {
    const  url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/files/template`;
    return this.http.get<PlanFile>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  softDeletePlanRegion(serviceTreeId: string, region: string, version: number, blueprint: string) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/softDelete`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  refreshCapacityOrdersByPlanRegionAsync(
    serviceTreeId: string,
    region: string,
    version: number,
    blueprint: string
  ): Observable<CapacityOrderRefreshResult[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/refresh`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  savePlanFile(serviceTreeId: string, planFile: PlanFile, name: string) {
    const  url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/files`;
    return this.http.post(url, planFile, this.httpOptions).pipe(catchError(this.handleError));
  }

  deletePlanFile(serviceTreeId: string, fileName: string, name: string) {
    const  url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/files/${fileName}`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  commitPlan(serviceTreeId: string, payload: PlanCommit, name: string): Observable<CommitPlan> {
    const  url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/commit`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  onboardNewPlan(serviceTreeId: string, payload: PlanOnboard) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/onboard`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  undoOnboardPlan(serviceTreeId: string, name: string) {
    const  url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/delete`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  copyToPlanV3(serviceTreeId: string, name: string) {
    const  url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/blueprints/${name}/v3`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  changePlanKind(serviceTreeId: string, name: string, payload: ChangePlanKind) {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/${name}/kind`;
    return this.http.patch(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanJsonSchema(): Observable<PlanJsonSchema> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/schema/get`;
    return this.http.get<PlanJsonSchema>(url, this.httpOptions);
  }

  getPlannedQuotaSubscriptionBindings(): Observable<PlannedQuotaSubscriptionBinding[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/bindings/subscriptions`;
    return this.http.get<PlannedQuotaSubscriptionBinding[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaPropertyBindings(): Observable<PlannedQuotaPropertyBinding[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/bindings/properties`;
    return this.http.get<PlannedQuotaPropertyBinding[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaSubscriptionBindingsByServiceTreeId(serviceTreeId: string): Observable<PlannedQuotaSubscriptionBinding[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/bindings/subscriptions`;
    return this.http.get<PlannedQuotaSubscriptionBinding[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaPropertyBindingsByServiceTreeId(serviceTreeId: string): Observable<PlannedQuotaPropertyBinding[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/bindings/properties`;
    return this.http.get<PlannedQuotaPropertyBinding[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addPlannedQuotaSubscriptionBindings(payload: PlannedQuotaSubscriptionBinding): Observable<IStatusResponse> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${payload.ServiceTreeId}/regions/${payload.Region}/bindings/subscriptions`;
    return this.http.post<PlannedQuotaSubscriptionBinding>(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  addPlannedQuotaPropertyBindings(payload: PlannedQuotaPropertyBinding): Observable<IStatusResponse> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${payload.ServiceTreeId}/regions/${payload.Region}/bindings/properties`;
    return this.http.post<PlannedQuotaPropertyBinding>(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  deletePlannedQuotaSubscriptionBindings(payload: PlannedQuotaSubscriptionBinding): Observable<IStatusResponse> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/regions/${payload.Region}/bindings/subscriptions/${payload.SubscriptionId}/blueprint/${payload.Blueprint}`;
    return this.http.delete<PlannedQuotaSubscriptionBinding>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  deletePlannedQuotaPropertyBindings(payload: PlannedQuotaPropertyBinding): Observable<IStatusResponse> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${payload.ServiceTreeId}/regions/${payload.Region}/blueprint/${payload.Blueprint}/bindings/subKey/${payload.SubscriptionKey}/key/${payload.BindingKey}/type/${payload.BindingType}/value/${payload.BindingValue}`;
    return this.http.delete<PlannedQuotaPropertyBinding>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanRegions(serviceTreeId: string, region: string, blueprint: string): Observable<PlanRegion[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/blueprints/${blueprint}`;
    return this.http.get<PlanRegion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlanRegionStatus(region: string, serviceTreeId: string, blueprint: string): Observable<PlanRegionStatus> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/blueprints/${blueprint}/status`;
    return this.http.get<PlanRegion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaSubscriptionBindingsByRegionAndTeam(region: string, serviceTreeId: string): Observable<PlannedQuotaSubscriptionBinding[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/bindings/subscriptions`;
    return this.http.get<PlannedQuotaSubscriptionBinding[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaPropertyBindingsByRegionAndTeam(region: string, serviceTreeId: string): Observable<PlannedQuotaPropertyBinding[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/bindings/properties`;
    return this.http.get<PlannedQuotaPropertyBinding[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlannedQuotaStatusByRegion(region: string): Observable<PlanRegionStatus[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/regions/${region}/status`;
    return this.http.get<PlanRegionStatus[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPlansRisks(): Observable<PlansRisks> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/risks`;
    return this.http.get<PlansRisks>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getIcmNotifications(parentRequestId: string, requestSerivceType: string): Observable<IcmNotification[]> {
    const url = `${environment.lionrockApiEndpoint}/api/requests/${parentRequestId}/icmnotification/${requestSerivceType}`;
    return this.http.get<IcmNotification[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getServiceReadinesses(): Observable<ServiceReadiness[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceReadiness`;
    return this.http.get<ServiceReadiness[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateServiceReadiness(data: ServiceReadiness): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceReadiness`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  createServiceReadiness(data: ServiceReadiness): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceReadiness`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteServiceReadiness(data: ServiceReadiness): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceReadiness/${data.Region}/${data.Service}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getServiceReadinessStages(): Observable<ServiceReadinessStage[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceReadinessStage`;
    return this.http.get<ServiceReadinessStage[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  createServiceReadinessStage(data: ServiceReadinessStage): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceReadinessStage`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteServiceReadinessStage(data: ServiceReadinessStage): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/serviceReadinessStage/${encodeURIComponent(data.Service)}/${encodeURIComponent(data.Region)}/${encodeURIComponent(data.Stage)}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getSqls(): Observable<Sql[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/sql`;
    return this.http.get<Sql[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateSql(data: Sql): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/sql`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  createSql(data: Sql): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/sql`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteSql(data: Sql): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/sql/${data.Region}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getArmResourceTypes(): Observable<ArmResourceType[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/armResourceType`;
    return this.http.get<ArmResourceType[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateArmResourceType(data: ArmResourceType): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/armResourceType`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  createArmResourceType(data: ArmResourceType): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/armResourceType`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteArmResourceType(data: ArmResourceType): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/armResourceType/${data.Namespace}/${data.ResourceTypeName}/${data.Location}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegions(): Observable<RegionTable[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/region`;
    return this.http.get<RegionTable[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getProjects(): Observable<Project[]> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/project`;
    return this.http.get<Project[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateProject(data: Project): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/project`;
    return this.http.put<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  createProject(data: Project): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/project`;
    return this.http.post<IStatusResponse>(url, data, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteProject(data: Project): Observable<IStatusResponse> {
    const url = `${NprConfiguration.getGenericReqUrl}/adminView/project/${data.ProjectOid}`;
    return this.http.delete<IStatusResponse>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getRegionalPlansAllVmSkus(): Observable<PlanDetail[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/regional/details`;
    return this.http.get<PlanDetail[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addCapacityOrderMessage(orderId: string, subOrderId: string, message: Message): Observable<string> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/capacity-order/orders/${orderId}/subOrders/${subOrderId}/message`;
    return this.http.post<string>(url, message, this.httpOptions).pipe(catchError(this.handleError));
  }

  getCapacitySubOrderDetails(orderId: string, subOrderId: string): Observable<CapacitySubOrderDetails> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/capacity-order/orders/${orderId}/subOrders/${subOrderId}`;
    return this.http.get<CapacitySubOrderDetails>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAlternatives(serviceTreeId: string, region: string, blueprint: string, version: number): Observable<Alternative[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/blueprints/${blueprint}/versions/${version}/alternatives`;
    return this.http.get<Alternative[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateAlternative(serviceTreeId: string, region: string, version: number, payload: UpdateAlternative): Observable<string> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/alternatives`;
    return this.http.post<string>(url, payload, this.httpOptions);
  }

  // comments related functions
  getComments(serviceTreeId: string, region: string, version: number, blueprint: string, topic: string): Observable<Comment[]> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/comments/topics/${topic}`;
    return this.http.get<Comment[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getLatestPfPlanComments(serviceTreeId: string): Observable<Comment[]> {
    const url = `${PfConfiguration.pfPlansUrl}/services/${serviceTreeId}/comments/latest`;
    return this.http.get<Comment[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addComment(serviceTreeId: string, region: string, version: number, blueprint: string | null, comment: Comment): Observable<void> {
    let url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}`;
    if (blueprint) {
      url += `/blueprints/${blueprint}`;
    }
    url += '/comments';
    return this.http.post<void>(url, comment, this.httpOptions).pipe(catchError(this.handleError));
  }

  getCommentTopics(
    serviceTreeId: string,
    region: string,
    version: number,
    blueprint: string
  ): Observable<{ DefaultCommentTopic: string; CommentTopics: string[] }> {
    const url = `${PlannedQuotaConfiguration.plansUrl}/services/${serviceTreeId}/regions/${region}/versions/${version}/blueprints/${blueprint}/comments/topics`;
    return this.http
      .get<{ DefaultCommentTopic: string; CommentTopics: string[] }>(url, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  // FFP plan related functions
  getAllFfpPlans(): Observable<FfpPlan[]> {
    return this.http.get<FfpPlan[]>(FfpConfiguration.ffpUrl, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyFfpPlans(): Observable<FfpPlan[]> {
    const url = `${FfpConfiguration.ffpUrl}/mine`;
    return this.http.get<FfpPlan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpPlan(serviceTreeId: string, type: string): Observable<FfpPlan> {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}`;
    return this.http.get<FfpPlan>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpPlansHistory(serviceTreeId: string, type: string): Observable<FfpPlan[]> {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/history`;
    return this.http.get<FfpPlan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  onboardNewFfpPlan(serviceTreeId: string, type: string) {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/onboard`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  undoOnboardFfpPlan(serviceTreeId: string, type: string) {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/delete`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpPlanFiles(serviceTreeId: string, type: string, version: number): Observable<FfpPlanFile[]> {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/files/versions/${version}`;
    return this.http.get<FfpPlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getLatestFfpPlanFiles(serviceTreeId: string, type: string): Observable<FfpPlanFile[]> {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/files/latest`;
    return this.http.get<FfpPlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpPlanFileTemplate(serviceTreeId: string): Observable<FfpPlanFile> {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/files/template`;
    return this.http.get<FfpPlanFile>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  saveFfpPlanFile(serviceTreeId: string, type: string, ffpPlanFile: FfpPlanFile) {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/files`;
    return this.http.post(url, ffpPlanFile, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteFfpPlanFile(serviceTreeId: string, type: string, fileName: string) {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/files/${fileName}`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  commitFfpPlan(serviceTreeId: string, type: string, payload: PlanCommit): Observable<CommitFfpPlan> {
    const url = `${FfpConfiguration.ffpPlansUrl}/services/${serviceTreeId}/types/${type}/commit`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpPlanJsonSchema(): Observable<PlanJsonSchema> {
    const url = `${FfpConfiguration.ffpPlansUrl}/schema/get`;
    return this.http.get<PlanJsonSchema>(url, this.httpOptions);
  }

  getLatestFfpInstances(): Observable<FfpPlanInstance[]> {
    const url = `${FfpConfiguration.ffpInstancesUrl}/latest`;
    return this.http.get<FfpPlanInstance[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  assignFfpPlanToProject(payload: FfpPlanSubmission) {
    const url = `${FfpConfiguration.ffpPlansUrl}/assign`;
    return this.http.post<FfpAssignPlanToProject>(url, payload, this.httpOptions);
  }

  getFfpPlanInstance(serviceTreeId: string, projectOid: string, version: number): Observable<FfpPlanInstance> {
    const url = `${FfpConfiguration.ffpInstancesUrl}/services/${serviceTreeId}/projects/${projectOid}/versions/${version}`;
    return this.http.get<FfpPlanInstance>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpPlanInstanceLatestVersion(serviceTreeId: string, projectOid: string): Observable<FfpPlanInstance> {
    const url = `${FfpConfiguration.ffpInstancesUrl}/services/${serviceTreeId}/projects/${projectOid}/latest`;
    return this.http.get<FfpPlanInstance>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpInstancePlanApprovals(serviceTreeId: string, projectOid: string, version: number): Observable<FfpPlanInstanceApproval[]> {
    const url = `${FfpConfiguration.ffpInstancesUrl}/services/${serviceTreeId}/projects/${projectOid}/versions/${version}/approvals`;
    return this.http.get<FfpPlanInstanceApproval[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  approveFfpPlanInstance(
    serviceTreeId: string,
    projectOid: string,
    ffpPlanInstanceOverallApproval: FfpPlanInstanceOverallApproval
  ): Observable<OkMessage> {
    const url = `${FfpConfiguration.ffpInstancesUrl}/services/${serviceTreeId}/projects/${projectOid}/approve`;
    return this.http.post<OkMessage>(url, ffpPlanInstanceOverallApproval, this.httpOptions).pipe(catchError(this.handleError));
  }

  rejectFfpPlanInstance(serviceTreeId: string, projectOid: string, ffpPlanRejectData: FfpPlanRejectData) {
    const url = `${FfpConfiguration.ffpInstancesUrl}/services/${serviceTreeId}/projects/${projectOid}/reject`;
    return this.http.post(url, ffpPlanRejectData, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpMyInstances(): Observable<FfpPlanInstanceDetail[]> {
    const url = `${FfpConfiguration.ffpInstancesUrl}/mine`;
    return this.http.get<FfpPlanInstanceDetail[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getLatestApprovedFfpPlanFiles(serviceTreeId: string, projectOid: string): Observable<FfpPlanFile[]> {
    const url = `${FfpConfiguration.ffpInstancesUrl}/services/${serviceTreeId}/projects/${projectOid}/files/latestApproved`;
    return this.http.get<FfpPlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  reprocessFfpPlanInstance(serviceTreeId: string, projectOid: string, version: number) {
    const url = `${FfpConfiguration.ffpInstancesUrl}/services/${serviceTreeId}/projects/${projectOid}/versions/${version}/reprocess`;
    return this.http.post<FfpAssignPlanToProject>(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  // project related functions
  getAllLatestFfpPlansByProject(projectOid: string): Observable<FfpPlanInstance[]> {
    const url = `${NprConfiguration.baseApiUrl}/projects/${projectOid}/plans`;
    return this.http.get<FfpPlanInstance[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpProjectBuildouts(): Observable<ProjectBuildout[]> {
    const url = `${NprConfiguration.baseApiUrl}/projects/buildout/all`;
    return this.http.get<ProjectBuildout[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getFfpProjectBuildout(projectOid: string): Observable<ProjectBuildout> {
    const url = `${NprConfiguration.baseApiUrl}/projects/${projectOid}/buildout`;
    return this.http.get<ProjectBuildout[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  newFfpBuildout(projectOid: string, psl: number) {
    const url = `${NprConfiguration.baseApiUrl}/projects/${projectOid}/buildout?pslId=${psl}`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  lockFfpProjectBuildout(projectOid: string) {
    const url = `${NprConfiguration.baseApiUrl}/projects/${projectOid}/lock`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  getProjectList(): Observable<Project[]> {
    const url = `${NprConfiguration.baseApiUrl}/projects/all`;
    return this.http.get<Project[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  syncFfpProjectBuildout(projectOid: string) {
    const url = `${NprConfiguration.baseApiUrl}/projects/${projectOid}/sync`;
    return this.http.post<FfpAssignPlanToProject[]>(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  getProject(projectOid: string): Observable<Project> {
    const url = `${NprConfiguration.baseApiUrl}/projects/${projectOid}`;
    return this.http.get<Project>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  // PF plan related functions
  getAllPfPlans(): Observable<PfPlan[]> {
    return this.http.get<PfPlan[]>(PfConfiguration.pfUrl, this.httpOptions).pipe(catchError(this.handleError));
  }

  getMyPfPlans(): Observable<PfPlan[]> {
    const url = `${PfConfiguration.pfUrl}/mine`;
    return this.http.get<PfPlan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfPlan(serviceTreeId: string, version: number): Observable<PfPlan> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/version/${version}`;
    return this.http.get<PfPlan>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfPlanLatestVersion(serviceTreeId: string): Observable<PfPlan> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}`;
    return this.http.get<PfPlan>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfPlansHistory(serviceTreeId: string): Observable<PfPlan[]> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/history`;
    return this.http.get<PfPlan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfPlanDetails(): Observable<PfPlanDetail[]> {
    const url = `${PfConfiguration.pfPlansUrl}/details/latest`;
    return this.http.get<PfPlanDetail[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  backfillPfPlanDetails(): Observable<any> {
    const url = `${PfConfiguration.pfPlansUrl}/details/backfill`;
    return this.http.get(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  onboardNewPfPlan(serviceTreeId: string) {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/onboard`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  undoOnboardPfPlan(serviceTreeId: string) {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/delete`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfPlanFiles(serviceTreeId: string, version: number): Observable<PfPlanFile[]> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/files/versions/${version}`;
    return this.http.get<PfPlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getLatestApprovedPfPlanFiles(serviceTreeId: string): Observable<PfPlanFile[]> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/files/latestApproved`;
    return this.http.get<PfPlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }
  getLatestPfPlanFiles(serviceTreeId: string): Observable<PfPlanFile[]> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/files/latest`;
    return this.http.get<PfPlanFile[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfPlanFileTemplate(serviceTreeId: string): Observable<PfPlanFile> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/files/template`;
    return this.http.get<PfPlanFile>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getApprovablePfPlans(): Observable<PfPlan[]> {
    const url = `${PfConfiguration.pfPlansUrl}/approvable`;
    return this.http.get<PfPlan[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  savePfPlanFile(serviceTreeId: string, pfPlanFile: PfPlanFile) {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/files`;
    return this.http.post(url, pfPlanFile, this.httpOptions).pipe(catchError(this.handleError));
  }

  deletePfPlanFile(serviceTreeId: string, fileName: string) {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/files/${fileName}`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  commitPfPlan(serviceTreeId: string, payload: PlanCommit): Observable<CommitPfPlan> {
    const url = `${PfConfiguration.pfUrl}/services/${serviceTreeId}/commit`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  approvePfPlan(serviceTreeId: string, version: number): Observable<OkMessage> {
    const url = `${PfConfiguration.pfPlansUrl}/services/${serviceTreeId}/approve`;
    return this.http.post(url, version, this.httpOptions).pipe(catchError(this.handleError));
  }

  rejectPfPlan(serviceTreeId: string, pfpLanRejectData: PfPlanRejectData) {
    const url = `${PfConfiguration.pfPlansUrl}/services/${serviceTreeId}/reject`;
    return this.http.post(url, pfpLanRejectData, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfPlanJsonSchema(): Observable<PlanJsonSchema> {
    const url = `${PfConfiguration.pfUrl}/schema/get`;
    return this.http.get<PlanJsonSchema>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getAllPfRequests(): Observable<PfRequest[]> {
    const url = `${PfConfiguration.pfUrl}/requests/all`;
    return this.http.get<PfRequest[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfRequest(id: number): Observable<PfRequest> {
    const url = `${PfConfiguration.pfUrl}/requests/${id}`;
    return this.http.get<PfRequest>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPfRequestApprovers(): Observable<string[]> {
    const url = `${PfConfiguration.pfUrl}/requests/approvers`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  createPfRequest(request: CreatePfRequest): Observable<IStatusResponse> {
    const url = `${PfConfiguration.pfUrl}/requests`;
    return this.http.post<IStatusResponse>(url, request, this.httpOptions).pipe(catchError(this.handleError));
  }

  approvePfRequest(id: number): Observable<OkMessage> {
    const url = `${PfConfiguration.pfUrl}/requests/${id}/approve`;
    return this.http.post<OkMessage>(url, null, this.httpOptions).pipe(catchError(this.handleError));
  }

  rejectPfRequest(id: number): Observable<OkMessage> {
    const url = `${PfConfiguration.pfUrl}/requests/${id}/reject`;
    return this.http.post<OkMessage>(url, null, this.httpOptions).pipe(catchError(this.handleError));
  }

  deletePfRequest(id: number): Observable<unknown> {
    const url = `${PfConfiguration.pfUrl}/requests/${id}`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getManatrees(): Observable<Manatree[]> {
    const url = ManatreeConfiguration.manatreeUrl;
    return this.http.get<Manatree[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getManatreeMappings(): Observable<Manatree[]> {
    const url = `${ManatreeConfiguration.manatreeUrl}/mapping`;
    return this.http.get<Manatree[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addManatree(manatree: Manatree): Observable<IStatusResponse> {
    const url = ManatreeConfiguration.manatreeUrl;
    return this.http.post<IStatusResponse>(url, manatree, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteManatree(manatree: Manatree): Observable<IStatusResponse> {
    const url = ManatreeConfiguration.manatreeUrl;
    return this.http.request<IStatusResponse>("delete", url, { ...this.httpOptions, body: manatree }).pipe(catchError(this.handleError));
  }

  async setEditModalData(data: any, func: (data: any) => IStatusResponse) {
    try {
      const response = func(data);
      return response;
    } catch (error) {
      this.handleError(error);
    }
  }

  getNamespaceList(): Observable<string[]> {
    const url = `${NprConfiguration.baseApiUrl}/namespaces/all`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  // Third-party customer management APIs
  getThirdPartySubscriptionCount(queryParameterName: string, queryParameterValue: string): Observable<number> {
    const url = `${NprConfiguration.baseApiUrl}/thirdparty-customers/subscription-count?${queryParameterName}=${queryParameterValue}`;
    return this.http.get<number>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  enableThirdPartyRegionAccess(payload: EnableRegionAccess): Observable<any>
  {
    const url = `${NprConfiguration.baseApiUrl}/thirdparty-customers/backfill`;
    return this.http.post(url, payload, this.httpOptions).pipe(catchError(this.handleError));
  }

  getThirdPartyAllowlists(): Observable<CustomerRegion[]>
  {
    const url = `${NprConfiguration.baseApiUrl}/thirdparty-customers/auto-allowlist`;
    return this.http.get<CustomerRegion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  deleteThirdPartyAllowlist(billingAccountId: string, region: string): Observable<any>
  {
    const url = `${NprConfiguration.baseApiUrl}/thirdparty-customers/${billingAccountId}/regions/${region}/auto-allowlist`;
    return this.http.delete(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  addThirdPartyAllowlist(billingAccountId: string, region: string): Observable<any>
  {
    const url = `${NprConfiguration.baseApiUrl}/thirdparty-customers/${billingAccountId}/regions/${region}/auto-allowlist`;
    return this.http.post(url, null, this.httpOptions).pipe(catchError(this.handleError));
  }

  getBackfilledSubscriptions(): Observable<SubscriptionRegion[]>
  {
    const url = `${NprConfiguration.baseApiUrl}/thirdparty-customers/subscriptions`;
    return this.http.get<SubscriptionRegion[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  // CapacityBlueprint related functions
  getCapacityBlueprints(): Observable<Blueprint[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/blueprints`;
    return this.http.get<Blueprint[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getCapacityBlueprintTypes(): Observable<string[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/blueprintTypes`;
    return this.http.get<string[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  updateCapacityBlueprints(): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/blueprints/refresh`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  fullRefreshCapacityBlueprints(): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/blueprints/full-refresh`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  assignCapacityBlueprintToProject(blueprintId: number, projectId: string): Observable<string> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/projects/${projectId}/blueprints/${blueprintId}`;
    return this.http.post(url, {}, { ...this.httpOptions, responseType: 'text' }).pipe(catchError(this.handleError));
  }

  assignPslToProject(pslId: string, projectId: number): Observable<string> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/projects/${projectId}/psl/${encodeURIComponent(pslId)}`;
    return this.http.post(url, {}, { ...this.httpOptions, responseType: 'text' }).pipe(catchError(this.handleError));
  }

  getPslAssignmentProjects(): Observable<PslAssignmentProject[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/psl-assignment/projects`;
    return this.http.get<PslAssignmentProject[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getPslServices(pslId: number, projectId: number): Observable<PslService[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/psl/${pslId}/services?projectId=${projectId}`;
    return this.http.get<PslService[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  assignBlueprintsToProject(projectId: number, blueprintIds: number[]): Observable<string> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/projects/${projectId}/blueprints/assign`;
    return this.http.post(url, blueprintIds, { ...this.httpOptions, responseType: 'text' }).pipe(catchError(this.handleError));
  }

  getDemands(): Observable<Demand[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands`;
    return this.http.get<Demand[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandsByStatus(status: string): Observable<Demand[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands?status=${encodeURIComponent(status)}`;
    return this.http.get<Demand[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandsByProjectId(projectId: number): Observable<Demand[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/projects/${projectId}/demands`;
    return this.http.get<Demand[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemand(demandId: number): Observable<Demand> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}`;
    return this.http.get<Demand>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandItems(): Observable<DemandItem[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demandItems`;
    return this.http.get<DemandItem[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  /** Basic demand item details by numeric id. */
  getDemandItem(demandItemId: number): Observable<DemandItem> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demandItems/${demandItemId}`;
    return this.http.get<DemandItem>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandItemsByDemandId(demandId: number): Observable<DemandItem[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}/items`;
    return this.http.get<DemandItem[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandItemAlternatives(): Observable<DemandItemAlternative[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demandItemAlternatives`;
    return this.http.get<DemandItemAlternative[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandItemProperties(): Observable<DemandItemProperty[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demandItemProperties`;
    return this.http.get<DemandItemProperty[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandSignoffs(): Observable<any[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demandsignoffs`;
    return this.http.get<any[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandSignoffsByDemandId(demandId: number): Observable<any[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}/signoffs`;
    return this.http.get<any[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  signoffDemand(demandId: number, itemAlternativeOrders: Record<number, number>): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}/signoffs`;
    return this.http
      .post(url, { ItemAlternativeOrders: itemAlternativeOrders }, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  rejectDemand(demandId: number): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}/signoffs/reject`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  removeDemand(demandId: number): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}/remove`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandItemExpanded(demandId: number): Observable<DemandItemExpanded[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}/items/expanded`;
    return this.http.get<DemandItemExpanded[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  getDemandItemExpandedByDemandItemId(demandItemId: number): Observable<DemandItemExpanded[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demand-items/${demandItemId}/expanded`;
    return this.http.get<DemandItemExpanded[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  refreshDemandItems(demandId: number): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/demands/${demandId}/items/refresh`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  // -------- Capacity history (see docs: logic-key-design.md §7) --------

  /** Basic blueprint details by numeric id. */
  getCapacityBlueprint(blueprintId: number): Observable<Blueprint> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/blueprints/${blueprintId}`;
    return this.http.get<Blueprint>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  /** Blueprint history by logical key, newest first. */
  getBlueprintHistory(serviceTreeId: string, type: string, name: string): Observable<Blueprint[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/serviceTreeId/${serviceTreeId}/type/${type}/name/${name}`;
    return this.http.get<Blueprint[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  /** Refreshes a single blueprint from the most recent commit that changed its bicep file. */
  refreshSingleBlueprint(serviceTreeId: string, type: string, name: string): Observable<any> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/blueprints/serviceTreeId/${serviceTreeId}/type/${type}/name/${name}/refresh`;
    return this.http.post(url, {}, this.httpOptions).pipe(catchError(this.handleError));
  }

  /** Demand history by logical key, newest first. */
  getDemandHistory(serviceTreeId: string, type: string, name: string, region: string): Observable<Demand[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/serviceTreeId/${serviceTreeId}/type/${type}/name/${name}/region/${region}`;
    return this.http.get<Demand[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }

  /** DemandItem history by logical key, newest first. */
  getDemandItemHistory(
    serviceTreeId: string,
    type: string,
    name: string,
    region: string,
    resource: string,
  ): Observable<DemandItem[]> {
    const url = `${NprConfiguration.baseApiUrl}/capacity/serviceTreeId/${serviceTreeId}/type/${type}/name/${name}/region/${region}/resource/${resource}`;
    return this.http.get<DemandItem[]>(url, this.httpOptions).pipe(catchError(this.handleError));
  }
}

export class Guid {
  constructor(public guid: string) { }

  // Static member
  static MakeNew(): Guid {
    let result: string;
    let i: string;
    let j: number;

    result = "";
    for (j = 0; j < 32; j++) {
      if (j === 8 || j === 12 || j === 16 || j === 20) {
        result = result + "-";
      }
      i = Math.floor(Math.random() * 16)
        .toString(16)
        .toUpperCase();
      result = result + i;
    }
    return new Guid(result);
  }

  public ToString(): string {
    return this.guid;
  }
}
